import { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import IconButton from "@shared/v2/IconButton";
import { CalendarCheckSolidV6, CommentSolidV6, EnvelopeSolidV6, PhoneSolidV6 } from "@shared/v2/Icomoon";
import OtherInteract from "../../People/components/OtherInteract";

import EventDetails from "./EventDetails";
import { eventTypeProps, getIconClass, LABEL_CATEGORIES } from "../utils/events";
import Tooltip from "../../shared/v2/Tooltip/Tooltip";
import { setCallParams } from "../../reducers/dialerReducer/dialerReducer";
import TaskModal from "../../Tasks/components/TaskModal";

const EventItem = ({
  event,
  event: {
    email_present: emailPresent,
    event_type: eventType,
    interaction,
    kind,
    managed_number: managedNumber,
    person,
    person_avatar: personAvatar,
    person_email: personEmail,
    person_phone_number: personPhoneNumber,
    time_ago: timeAgo,
    timeline_fields: timelineFields,
  },
  openSmsModalWithPerson,
}) => {
  const dispatch = useDispatch();
  const launchEmailModal = () => {
    if (emailPresent) Brivity.EmailInteraction.showModal(person.id);
  };

  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  const otherEventInteraction = {
    ...interaction,
    interaction_type: "other",
    interacted_at: new Date(),
  };
  const propertyObject = timelineFields?.property || {};
  const iconClass = getIconClass(kind);
  const bedString = !propertyObject.bedrooms ? "" : `${propertyObject.bedrooms} Bed`;
  const bathString = !propertyObject.bathrooms ? "" : `${propertyObject.bathrooms} Bath`;
  const bedBathString = `${bedString} | ${bathString}`.trim();

  if (!timelineFields) return null;
  return (
    <div className="tw-flex tw-py-[20px] tw-min-h-[191px] tw-border-solid tw-border-neutral-gray-5 tw-border-0 tw-border-b-[1px]">
      <span className="fa-stack fa-lg tw-mr-[15px] tw-mt-[-8px] tw-w-[40px] tw-h-[40px] tw-flex-none">
        <i className="fa fa-circle fa-stack-2x" />
        <i
          className={`tw-text-[22px] tw-text-gray-75 tw-bg-white tw-p-[0px] tw-border-solid tw-border-[2px] tw-border-gray-75 tw-rounded-full fa fa-${iconClass} fa-stack-1x `}
        />
      </span>

      <div className="tw-flex tw-justify-between tw-gap-[8px] tw-w-full">
        <div className="tw-flex-1 tw-flex tw-flex-col tw-min-w-[200px] tw-max-w-[360px]">
          <div>
            <a href={`/people/${person.slug}`}>
              <span className="tw-text-gray-75 tw-font-bold">
                {person.first_name} {person.last_name}
              </span>
            </a>{" "}
            <span>{LABEL_CATEGORIES[kind]}</span>;
          </div>
          <div id="detail-interaction-buttons" className="tw-py-[8px] tw-flex tw-gap-[4px]">
            <Tooltip
              innerClassName="tw-max-w-[500px]"
              placement="right"
              trigger={
                <IconButton onClick={launchEmailModal} disabled={!emailPresent}>
                  <EnvelopeSolidV6 />
                </IconButton>
              }
              content={personEmail || ""}
            />
            <Tooltip
              innerClassName="tw-max-w-[500px]"
              placement="right"
              trigger={
                <IconButton
                  onClick={() =>
                    dispatch(setCallParams({ leadUuids: [person.uuid], callSource: "Dashboard" }))
                  }
                  disabled={!personPhoneNumber}
                >
                  <PhoneSolidV6 />
                </IconButton>
              }
              content={personPhoneNumber || ""}
            />
            <Tooltip
              innerClassName="tw-max-w-[500px]"
              placement="right"
              trigger={
                <IconButton
                  disabled={!managedNumber || !personPhoneNumber}
                  onClick={() => openSmsModalWithPerson({ data: { attributes: person } })}
                >
                  <CommentSolidV6 />
                </IconButton>
              }
              content={managedNumber && personPhoneNumber}
            />
            <Tooltip
              innerClassName="tw-max-w-[500px]"
              placement="right"
              trigger={
                <IconButton onClick={() => setShowAppointmentModal(true)}>
                  <CalendarCheckSolidV6 />
                </IconButton>
              }
              content="Log Appointment"
            />
            <OtherInteract
              otherInteract={{
                person,
                personAvatar,
                personFirstName: person.first_name,
                personLastName: person.last_name,
                interaction: otherEventInteraction,
              }}
            />
          </div>
          <EventDetails event={event} />
        </div>

        {eventType !== "ads-remarket" && (
          <div
            className="tw-self-center"
            role="button"
            tabIndex={0}
            onClick={() => timelineFields.property?.url && window.open(timelineFields.property.url)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && timelineFields.property?.url) {
                window.open(timelineFields.property.url);
              }
            }}
          >
            {timelineFields.property?.main_photo && (
              <div
                className="tw-relative tw-cursor-pointer tw-w-[260px] tw-h-[150px] tw-bg-cover"
                style={{
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${timelineFields.property.main_photo}`,
                }}
              >
                <div className="tw-absolute tw-bottom-[20px] tw-left-[20px] tw-w-[80%] tw-text-shadow-bootstrap-2 tw-text-white">
                  <span className="tw-text-[24px]">{`$ ${timelineFields.property.value} `}</span>
                  <span>{timelineFields.property.street_address}</span>
                  <span>
                    {` ${timelineFields.property.city}, ${timelineFields.property.state} ${timelineFields.property.zip} `}
                  </span>
                  <span>{bedBathString}</span>
                </div>
              </div>
            )}
          </div>
        )}
        {showAppointmentModal && (
          <TaskModal
            id="log-dashboard-appointment"
            isCreatingAppointment
            onClose={() => setShowAppointmentModal(false)}
            showTaskFor={false}
            taskFor={person}
            person={person}
            contactEmail={personEmail}
            taskable={person}
          />
        )}
        <div className="tw-whitespace-nowrap">
          <label>{timeAgo}</label>
        </div>
      </div>
    </div>
  );
};

EventItem.propTypes = {
  event: eventTypeProps.isRequired,
  openSmsModalWithPerson: PropTypes.func.isRequired,
};

export default EventItem;
